import {Button, Spinner} from "reactstrap";
import "./SiretLookupFeedback.scss";

export type SiretAutofillPhase = "prompt" | "loading" | "success" | "problem" | "revealed";

type Props = {
  phase: SiretAutofillPhase;
  // Legal name of a matched company (shown in the success flash).
  matchedName: string | null;
  problemKind: "not-found" | "closed" | null;
  // Acknowledge a problem and proceed to manual entry / continue anyway.
  onContinue: () => void;
};

/**
 * Guided feedback under the SIRET field, one state at a time:
 * - `loading`  → a prominent "searching" block;
 * - `success`  → a big "company found" flash (the parent reveals the fields once
 *                it auto-advances to `revealed`);
 * - `problem`  → a reassuring not-found / radiée message with a "continue anyway"
 *                action (the user can also just correct the SIRET above);
 * - `prompt`   → invites entering the SIRET first.
 * `revealed` renders nothing here — the parent shows the identity fields.
 */
export function SiretLookupFeedback({phase, matchedName, problemKind, onContinue}: Props) {
  if (phase === "prompt") {
    return (
      <p className="text-muted" data-testid="siret-prompt">
        {"Renseignez votre SIRET pour pré-remplir l'identité de l'entreprise."}
      </p>
    );
  }

  if (phase === "loading") {
    return (
      <div className="siret-feedback-block" data-testid="siret-loading">
        <Spinner />
        <span className="mt-2 text-muted">{"Recherche de votre entreprise…"}</span>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div
        className="siret-feedback-block siret-feedback-block--success text-success"
        data-testid="siret-success"
      >
        <CheckIcon />
        <div className="h5 mt-2 mb-0">Entreprise trouvée</div>
        {matchedName && <div className="fw-medium">{matchedName}</div>}
      </div>
    );
  }

  if (phase === "problem") {
    const closed = problemKind === "closed";
    return (
      <div
        className={`siret-callout siret-callout--${closed ? "warning" : "info"} siret-feedback--shake`}
        data-testid={closed ? "siret-closed-warning" : "siret-not-found"}
      >
        <div className="siret-callout__content">
          <span className="siret-callout__icon">{closed ? <WarningIcon /> : <InfoIcon />}</span>
          <p className="siret-callout__text">
            {closed
              ? "D'après l'INSEE, ce SIRET n'est plus actif (établissement fermé ou entreprise cessée). Vérifiez le numéro ci-dessus, ou continuez si c'est voulu."
              : "Nous n'avons pas trouvé d'entreprise pour ce SIRET. Il est peut-être trop récent ou non diffusé publiquement. Vérifiez le numéro, ou saisissez les informations vous-même."}
          </p>
        </div>
        <Button
          color="primary"
          onClick={onContinue}
          data-testid="siret-continue"
          className="siret-callout__cta"
        >
          {closed ? "Continuer quand même" : "Saisir manuellement"}
        </Button>
      </div>
    );
  }

  return null; // revealed → the parent renders the identity fields
}

function CheckIcon() {
  return (
    <svg className="siret-feedback__check" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <circle
        className="siret-feedback__ring"
        cx="12"
        cy="12"
        r="11"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        className="siret-feedback__tick"
        d="M7 12.5l3.2 3.3L17 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden style={{flex: "0 0 auto"}}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="7.5" r="0.7" fill="currentColor" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden style={{flex: "0 0 auto"}}>
      <path
        d="M12 3l9 16H3z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M12 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="17" r="0.7" fill="currentColor" />
    </svg>
  );
}
